import { toastr } from 'react-redux-toastr';
import UserLocal from '../../configuracao/userLocal';
import axiosInstance from '../../main/axios';

class NotificationService {
  constructor() {
    this.cache = new Map();
    this.lastProcessedTime = 0;
  }

  // Otimizada: O(n) ao invés de O(n²)
  detectNewNotifications(currentErrors, previousErrors) {
    if (!Array.isArray(currentErrors)) return [];
    if (!Array.isArray(previousErrors)) return currentErrors;

    // Criar Map para lookup O(1)
    const previousMap = new Map();
    previousErrors.forEach(erro => {
      const key = this.getErrorKey(erro);
      previousMap.set(key, erro);
    });

    // Filtrar novos erros
    return currentErrors.filter(erro => {
      const key = this.getErrorKey(erro);
      return !previousMap.has(key);
    });
  }

  // Chave do controlador físico (todos os anéis compartilham grupo + numero)
  getControllerGroupKey(erro) {
    const grupo = erro.grupo_fisico_id != null ? erro.grupo_fisico_id : '';
    const numero = erro.numero_controlador != null ? erro.numero_controlador : (erro.controlador_id || '');
    return `${grupo}-${numero}`;
  }

  // Chave única para erro
  getErrorKey(erro) {
    return `${this.getControllerGroupKey(erro)}-${erro.data_erro}-${erro.hora_erro}-${erro.mensagem}`;
  }

  // Garantir apenas uma notificação por controlador físico (mais recente)
  getUniqueNotificationsByController(errors) {
    if (!Array.isArray(errors)) return [];

    const controladorMap = new Map();
    
    errors.forEach(erro => {
      const groupKey = this.getControllerGroupKey(erro);
      const errorDateTime = new Date(`${erro.data_erro} ${erro.hora_erro}`);

      if (!controladorMap.has(groupKey) || 
          errorDateTime > controladorMap.get(groupKey).datetime) {
        controladorMap.set(groupKey, {
          erro: erro,
          datetime: errorDateTime,
        });
      }
    });

    return Array.from(controladorMap.values()).map(item => item.erro);
  }

  // Validar estrutura dos erros
  validateErrors(errors) {
    if (!Array.isArray(errors)) return [];
    
    return errors.filter(e => 
      e && 
      typeof e === 'object' && 
      (e.controlador_id || e.numero_controlador) && 
      e.data_erro && 
      e.hora_erro
    );
  }

  // Processar lista completa de erros
  processErrorList(erros, previousNotifications = []) {
    // 1. Validar
    const validatedErrors = this.validateErrors(erros);
    
    // 2. Garantir unicidade por controlador
    const uniqueErrors = this.getUniqueNotificationsByController(validatedErrors);
    
    // 3. Detectar novos
    const newErrors = this.detectNewNotifications(uniqueErrors, previousNotifications);
    
    return {
      allNotifications: uniqueErrors,
      newNotifications: newErrors,
      hasNewErrors: newErrors.length > 0
    };
  }

  // Verificar se usuário prefere modal
  shouldShowModal() {
    return UserLocal.getUserConfig('alerta_tela') === 1;
  }

  // Marcar notificação como lida (API call) - será substituído por integração Redux
  async markAsRead(notifications, clearAlertaErroAction = null) {
    if (!Array.isArray(notifications)) {
      notifications = [notifications];
    }

    try {
      if (clearAlertaErroAction) {
        // Usar action Redux original do sistema
        const result = await clearAlertaErroAction(notifications);
        return result;
      } else {
        // Fallback - simular sucesso (para compatibilidade)
        console.warn('markAsRead: clearAlertaErroAction não fornecida');
        return { success: true };
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      throw error;
    }
  }

  // Salvar no localStorage
  saveToLocalStorage(notifications) {
    try {
      const data = {
        notifications: notifications || [],
        timestamp: Date.now()
      };
      localStorage.setItem('_ssat_notifications', JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao salvar notificações:', error);
    }
  }

  // Carregar do localStorage
  loadFromLocalStorage() {
    try {
      const data = localStorage.getItem('_ssat_notifications');
      if (!data) return [];
      
      const parsed = JSON.parse(data);
      
      // Validar se não está muito antigo (24h)
      const now = Date.now();
      const dayInMs = 24 * 60 * 60 * 1000;
      
      if (now - parsed.timestamp > dayInMs) {
        this.clearLocalStorage();
        return [];
      }
      
      return parsed.notifications || [];
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      return [];
    }
  }

  // Limpar localStorage
  clearLocalStorage() {
    try {
      localStorage.removeItem('_ssat_notifications');
    } catch (error) {
      console.error('Erro ao limpar notificações:', error);
    }
  }

  // Refresh da API após marcar como lida
  async refreshNotifications() {
    try {
      const email = UserLocal.getUserConfig('email');
      if (!email) return [];
      
      const response = await axiosInstance.get(`/controladoreserro/${email}`);
      return response.data || [];
    } catch (error) {
      console.error('Erro ao atualizar notificações:', error);
      return [];
    }
  }
}

// Singleton
const notificationService = new NotificationService();

export default notificationService;